import { type Maybe, day, sortBy, utc } from '@vrplatform/utils';
import type { HasuraClient } from '@vrplatform/graphql';
import {
  whereListingPeriod,
  whereListings,
  whereRecurringFees,
} from '../queries';
import {
  formatOwnerName,
  getListingAddress,
  getListingName,
  getSourceDescription,
} from '../utils';

export const listingsCsvQuery = async (
  client: HasuraClient,
  input: Parameters<typeof whereListings>[0]
) => {
  const today = day().yyyymmdd();

  const { listings, classNames, recurringFees } = await client.query((q) => {
    const listings = q
      .listings({
        where: whereListings(input),
        order_by: [{ calculated_title: 'asc_nulls_first' }],
      })
      .map((listing) => {
        const ownerships = input.GL
          ? []
          : listing
              .ownerships({
                order_by: [{ owner: { firstName: 'asc_nulls_first' } }],
                where: {
                  newOwnerId: { _is_null: false },
                },
              })
              .map((ownership) => {
                return {
                  ownerName:
                    formatOwnerName(ownership.newOwner, {
                      lastNameFirst: false,
                    }) || 'No name',
                };
              });

        const currentOwnershipPeriod = !input.GL
          ? undefined
          : listing
              .ownershipPeriods({
                limit: 1,
                where: whereListingPeriod({
                  date: today,
                  type: 'ownership',
                }),
              })
              .map((period) => ({
                id: period.id,
                startAt: period.startAt as string,
                endAt: period.endAt as Maybe<string>,
                members: period
                  .members({
                    order_by: [
                      {
                        owner: {
                          name: 'asc_nulls_last',
                        },
                      },
                    ],
                  })
                  .map((owner) => ({
                    id: owner.id as string,
                    ownerId: owner.contactId as string,
                    ownerName: formatOwnerName(owner.contact),
                    ownerSplit: owner.split as number | undefined | null,
                    ownerType: owner.contact?.companyType
                      ? ('company' as const)
                      : ('individual' as const),
                  })),
              }))[0];

        const currentRecurringFeeSubscriptions = !input.GL
          ? []
          : listing
              .recurringFeeSubscriptions({
                where: {
                  _or: [
                    {
                      startAt: { _lte: today },
                      endAt: { _gte: today },
                    },
                    {
                      startAt: { _lte: today },
                      endAt: { _is_null: true },
                    },
                  ],
                },
              })
              .map((subscripton) => ({
                id: subscripton.id as string,
                startAt: subscripton.startAt as string,
                endAt: subscripton.endAt as Maybe<string>,
                recurringFeeId: subscripton.recurringFeeId as string,
              }));

        return {
          id: listing.id,
          name: getListingName(listing),
          address: getListingAddress(listing).full || '',
          ownerships: sortBy(ownerships, 'ownerName', 'asc'),
          group: listing.collection?.name || '',
          currentRecurringFeeSubscriptions,
          currentOwnershipPeriod,
        };
      });

    const classNames = input.GL
      ? []
      : q
          .source({
            where: {
              tenantId: { _eq: input.tenantId },
              type: { _eq: 'class' },
            },
          })
          .map((source) => ({
            id: source.id,
            description: getSourceDescription(source),
          }));

    const recurringFees = !input.GL
      ? []
      : q
          .recurringFees({
            where: whereRecurringFees({
              tenantId: input.tenantId,
            }),
            order_by: [
              {
                type: 'desc_nulls_last',
              },
              {
                title: 'asc_nulls_last',
              },
            ],
          })
          .map((fee) => ({
            id: fee.id,
            title: fee.title || 'No name',
          }));

    return {
      listings,
      classNames,
      recurringFees,
    };
  });

  const settings = input.GL
    ? []
    : await client.query((q) =>
        q
          .setting({
            where: {
              tenant_id: { _eq: input.tenantId },
              target: { _in: listings.map((i) => i.id) },
              right_id: { _is_null: false },
              leftType: { _in: ['finalytic.listing', 'ximplifiQbo'] },
              key: { _eq: 'class' },
            },
          })
          .map((setting) => ({
            id: setting.id,
            target: setting.target,
            sourceDescription: setting.source?.description,
            rightId: setting.right_id || setting.value,
          }))
      );

  const rows = listings.map((listing) => {
    const row: Record<string, string> = {
      Name: listing.name,
      Address: listing.address,
      'Listing Group': listing.group,
    };

    if (!input.GL) {
      row.Owners = listing.ownerships.map((i) => i.ownerName).join(', ');

      const setting = settings.find((i) => i.target === listing.id);
      row['Class Name'] =
        classNames.find((i) => i.id === setting?.rightId)?.description || '';
    } else {
      row['Current Ownership'] = ((): string => {
        if (!listing.currentOwnershipPeriod) return '';

        const owners = listing.currentOwnershipPeriod.members
          .map((member) => `${member.ownerName} (${member.ownerSplit})`)
          .join(', ');

        const start = utc(listing.currentOwnershipPeriod.startAt).yyyymmdd();
        const end = listing.currentOwnershipPeriod.endAt
          ? utc(listing.currentOwnershipPeriod.endAt).yyyymmdd()
          : 'forever';

        const period = [start, end].filter(Boolean).join(' - ');

        return `${period}${owners?.length ? `; ${owners}` : ''}`;
      })();

      if (recurringFees.length > 0) {
        for (const recurringFee of recurringFees) {
          if (recurringFee.title) {
            let status = 'disabled';

            const subscription = listing.currentRecurringFeeSubscriptions.find(
              (sub) => sub.recurringFeeId === recurringFee.id
            );

            if (subscription) {
              if (subscription.endAt) {
                status = `ending at ${utc(subscription.endAt).format('LL')}`;
              } else {
                status = 'enabled';
              }
            }

            row[recurringFee.title] = status;
          }
        }
      }
    }

    return row;
  });

  return {
    rows,
  };
};
